/**
 * ═══════════════════════════════════════════════════════════════════════════
 * createAccountPowerSyncQuery — chokepoint for PER-ACCOUNT queries
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * PROBLEM (cross-account leak): TanStack Solid Query uses ONE global cache,
 * shared between per-tab accounts. If two accounts request the
 * SAME task/list id with the SAME account-agnostic queryKey, the row
 * cached under account A is served to account B → data leak
 * ("first NOT FOUND and then it loads the other account's task").
 *
 * SOLUTION (transparent, single point): this wrapper PREFIXES the active account
 * per-tab in the queryKey automatically —
 *
 *     queryKey → ["acct", <active account>, ...agnosticKey]
 *
 * The prefix is read inside the key's THUNK (via `activeAccountUserId()`,
 * reactive to tab pin), so it follows an in-app account switch
 * (account switch without reload or URL change). Callers pass the NORMAL queryKey
 * (from `queryKeys.*`) and NEVER mention the userId.
 *
 * RULE: every account-bound data query (task detail, subtasks,
 * attachments, list detail/organization, search, my-day, etc.) MUST use
 * this wrapper. GLOBAL catalog queries (themes.catalog, fonts.*) and
 * server queries (auth.*) do NOT — they use the `createPowerSyncQuery` primitive directly.
 */

import type { Accessor } from "solid-js";
import type { QueryKey } from "@tanstack/solid-query";
import {
  createPowerSyncQuery,
  type CreatePowerSyncQueryArgs,
  type CreatePowerSyncQueryResult,
  createPowerSyncSingle,
  type PowerSyncTableRecord,
} from "@listto/shared/solid";
import { activeAccountUserId } from "@/lib/auth";

/** Stable prefix that marks a key as account-partitioned. */
export const ACCOUNT_KEY_PREFIX = "acct" as const;

function isAccessor<V>(value: Accessor<V> | V): value is Accessor<V> {
  return typeof value === "function";
}

/**
 * Builds the account-partitioned queryKey. Also reusable by
 * cache-only readers (`queryClient.getQueryData`): it MUST use EXACTLY the
 * same prefix as the wrapper or the reader would not find the row it wrote.
 */
export function accountScopedKey(userId: string | undefined, base: QueryKey): QueryKey {
  return [ACCOUNT_KEY_PREFIX, userId, ...base];
}

/** Wraps the caller's `queryKey` with the active-account prefix. */
function withAccountScope(rawKey: Accessor<QueryKey> | QueryKey): Accessor<QueryKey> {
  return () => {
    const base = isAccessor(rawKey) ? rawKey() : rawKey;
    return accountScopedKey(activeAccountUserId(), base);
  };
}

/**
 * `createPowerSyncQuery` with AUTOMATIC account scoping. Pass the queryKey
 * account-agnostic (e.g. `queryKeys.subtasks.byTodo(todoId)`); the wrapper
 * injects `["acct", <active account>, …]` internally.
 */
export function createAccountPowerSyncQuery<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
): CreatePowerSyncQueryResult<T> {
  return createPowerSyncQuery<T>({ ...args, queryKey: withAccountScope(args.queryKey) });
}

/** Single-row variant of `createAccountPowerSyncQuery`. */
export function createAccountPowerSyncSingle<T extends PowerSyncTableRecord>(
  args: CreatePowerSyncQueryArgs<T>,
) {
  return createPowerSyncSingle<T>({ ...args, queryKey: withAccountScope(args.queryKey) });
}
